import React, { useState, useEffect } from 'react';
import * as Icons from 'react-icons/fa';
import { categories, Drug, getDrugColor } from '../data/drugs';
import { useConsumptionLog } from '../hooks/useConsumptionLog';
import { useDrugs } from '../contexts/DrugsContext';

const LogEntryForm: React.FC = () => {
  const { addEntry } = useConsumptionLog();
  const { drugs, addCustomDrug } = useDrugs();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDrug, setSelectedDrug] = useState<string>('');
  const [dosage, setDosage] = useState<string>('');
  const [unit, setUnit] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showCustomDrugForm, setShowCustomDrugForm] = useState<boolean>(false);
  const [customDrugName, setCustomDrugName] = useState<string>('');
  const [customDrugUnit, setCustomDrugUnit] = useState<string>('mg');
  const [filteredDrugs, setFilteredDrugs] = useState<Drug[]>([]);

  // Update filtered drugs whenever category or drugs list changes
  useEffect(() => {
    if (selectedCategory) {
      const filtered = drugs.filter(drug => drug.category === selectedCategory);
      setFilteredDrugs(filtered);
      
      // Auto-select the drug if it's the only one in the category
      if (filtered.length === 1 && !selectedDrug) {
        setSelectedDrug(filtered[0].id);
        setUnit(filtered[0].defaultUnit);
      }
    } else {
      setFilteredDrugs([]);
    }
  }, [selectedCategory, drugs, selectedDrug]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedDrug('');
    setUnit('');
    setShowCustomDrugForm(false);
  };

  const handleDrugChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const drugId = e.target.value;
    
    if (drugId === 'add-custom' && selectedCategory === 'other') {
      setShowCustomDrugForm(true);
      setSelectedDrug('');
      return;
    }
    
    setSelectedDrug(drugId);
    setShowCustomDrugForm(false);
    
    const selectedDrugInfo = drugs.find(d => d.id === drugId);
    if (selectedDrugInfo) {
      setUnit(selectedDrugInfo.defaultUnit);
    }
  };

  const handleAddCustomDrug = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling up to parent form
    
    if (!customDrugName || !customDrugUnit) return;
    
    // Make sure we're in the "Other" category first
    setSelectedCategory('other');
    
    // Add the custom drug using the context
    const newDrug = addCustomDrug({
      name: customDrugName,
      category: 'other',
      defaultUnit: customDrugUnit,
      description: 'Custom drug'
    });
    
    // Reset form
    setCustomDrugName('');
    setCustomDrugUnit('mg');
    setShowCustomDrugForm(false);
    
    // Set the selected drug after a small delay to ensure state updates
    setTimeout(() => {
      setSelectedDrug(newDrug.id);
      setUnit(newDrug.defaultUnit);
    }, 10);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDrug || !dosage) return;
    
    addEntry({
      drugId: selectedDrug,
      dosage: parseFloat(dosage),
      unit,
      notes
    });
    
    // Reset form
    setSelectedDrug('');
    setDosage('');
    setNotes('');
    setIsOpen(false);
    setShowCustomDrugForm(false);
  };

  const renderColoredOptions = () => {
    return (
      <>
        <option value="">Select drug</option>
        {filteredDrugs.map(drug => {
          const drugColor = getDrugColor(drug.id);
          const colorClass = drugColor.replace('bg-', 'text-');
          return (
            <option key={drug.id} value={drug.id} className={colorClass}>
              {drug.name}
            </option>
          );
        })}
        {selectedCategory === 'other' && (
          <option value="add-custom">+ Add custom drug</option>
        )}
      </>
    );
  };

  return (
    <div className="mb-4 sm:mb-6">
      {!isOpen ? (
        <button 
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center w-full py-3 sm:py-4 bg-purple-700 hover:bg-purple-600 text-white rounded-lg transition-colors text-sm sm:text-base"
        >
          {React.createElement(Icons.FaPlus, { className: "mr-2" })}
          Log consumption
        </button>
      ) : (
        <div className="bg-gray-800 p-3 sm:p-4 rounded-lg shadow-lg">
          <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Log consumption</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="block text-gray-300 mb-1 text-sm">Category</label>
                <div className="relative">
                  {selectedCategory && (
                    <div 
                      className={`absolute left-0 top-0 bottom-0 w-1.5 ${categories.find(c => c.id === selectedCategory)?.color || 'bg-gray-700'}`}
                      style={{ borderTopLeftRadius: '0.25rem', borderBottomLeftRadius: '0.25rem' }}
                    ></div>
                  )}
                  <select 
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className="w-full p-2 sm:p-2.5 bg-gray-700 border border-gray-600 rounded text-white text-sm sm:text-base"
                    required
                    style={selectedCategory ? { paddingLeft: '0.75rem' } : {}}
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option 
                        key={category.id} 
                        value={category.id}
                        className={category.color.replace('bg-', 'text-')}
                      >
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 mb-1 text-sm">Drug</label>
                <div className="relative">
                  {selectedDrug && (
                    <div 
                      className={`absolute left-0 top-0 bottom-0 w-1.5 ${getDrugColor(selectedDrug)}`}
                      style={{ borderTopLeftRadius: '0.25rem', borderBottomLeftRadius: '0.25rem' }}
                    ></div>
                  )}
                  <select 
                    value={selectedDrug}
                    onChange={handleDrugChange}
                    className="w-full p-2 sm:p-2.5 bg-gray-700 border border-gray-600 rounded text-white text-sm sm:text-base"
                    disabled={!selectedCategory}
                    required
                    style={selectedDrug ? { paddingLeft: '0.75rem' } : {}}
                  >
                    {renderColoredOptions()}
                  </select>
                </div>
              </div>
            </div>
            
            {showCustomDrugForm && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-700 rounded-lg border border-gray-600 relative overflow-hidden">
                <div className="absolute left-0 top-0 w-full h-1 bg-gray-700"></div>
                <h4 className="text-base sm:text-lg font-semibold text-white mb-3">Add custom drug</h4>
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label className="block text-gray-300 mb-1 text-sm">Drug Name</label>
                      <input 
                        type="text"
                        value={customDrugName}
                        onChange={(e) => setCustomDrugName(e.target.value)}
                        className="w-full p-2 sm:p-2.5 bg-gray-600 border border-gray-500 rounded text-white text-sm sm:text-base"
                        placeholder="Custom drug name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-300 mb-1 text-sm">Unit</label>
                      <select 
                        value={customDrugUnit}
                        onChange={(e) => setCustomDrugUnit(e.target.value)}
                        className="w-full p-2 sm:p-2.5 bg-gray-600 border border-gray-500 rounded text-white text-sm sm:text-base"
                        required
                      >
                        <option value="mg">mg</option>
                        <option value="g">g</option>
                        <option value="ml">ml</option>
                        <option value="μg">μg</option>
                        <option value="pill">pill</option>
                        <option value="hit">hit</option>
                        <option value="puff">puff</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <button 
                      type="button"
                      onClick={() => setShowCustomDrugForm(false)}
                      className="min-w-[80px] px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-500 text-sm"
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      onClick={handleAddCustomDrug}
                      className="min-w-[80px] px-3 py-1.5 bg-purple-700 text-white rounded hover:bg-purple-600 text-sm"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-300 mb-1 text-sm">Dosage</label>
              <div className="flex">
                <input 
                  type="number"
                  value={dosage}
                  onChange={(e) => setDosage(e.target.value)}
                  className="flex-1 p-2 sm:p-2.5 bg-gray-700 border border-gray-600 rounded-l text-white text-sm sm:text-base"
                  placeholder="Amount"
                  min="0"
                  step="any"
                  required
                />
                <div className="bg-gray-600 px-3 flex items-center rounded-r border-t border-r border-b border-gray-600 text-sm sm:text-base">
                  <span className="text-gray-300">{unit}</span>
                </div>
              </div>
            </div>
            
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-300 mb-1 text-sm">Notes (Optional)</label>
              <textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 sm:p-2.5 bg-gray-700 border border-gray-600 rounded text-white h-16 sm:h-20 text-sm sm:text-base"
                placeholder="Add any notes about this dose..."
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <button 
                type="button"
                onClick={() => setIsOpen(false)}
                className="min-w-[80px] px-3 py-1.5 sm:py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="min-w-[80px] px-3 py-1.5 sm:py-2 bg-purple-700 text-white rounded hover:bg-purple-600 text-sm"
              >
                Log entry
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LogEntryForm; 